import React, { useEffect, useState } from 'react';

import { Box, getCSSVariable, Link, Text } from '@opendoor/bricks-next';
import { IconExclamationCircle } from '@tabler/icons-react';
import { useRouter } from 'next/router';

import { useObservability } from '../../../../helpers/observability';

// Read UTM params from window.location instead of router.query because
// the Cloudflare Worker strips UTM params from the SSR cache key (COSMOS_PARAM_DENYLIST),
// so they are never present in __NEXT_DATA__.query.
const isSellerReferral = (): boolean => {
  if (typeof window === 'undefined') return false;
  const params = new URLSearchParams(window.location.search);
  return params.get('utm_source') === 'seller' && params.get('utm_campaign') === 'ae_pdp';
};

export const RentalScamBanner: React.FC = () => {
  const router = useRouter();
  const { trackJourneyEvent } = useObservability();
  const [visible, setVisible] = useState(false);
  const addressUuid = Array.isArray(router.query.address_id)
    ? router.query.address_id[0]
    : router.query.address_id ?? '';

  useEffect(() => {
    setVisible(isSellerReferral());
  }, []);

  useEffect(() => {
    if (!visible) return;
    trackJourneyEvent('marketplaceProperty', 'rentalScamBannerView', {
      addressUuid,
    });
  }, [visible, addressUuid, trackJourneyEvent]);

  if (!visible) return null;

  return (
    <Box
      backgroundColor="$backgroundStateErrorSecondary"
      borderRadius="$8x"
      padding="$8x"
      flexDirection="row"
      gap="$4x"
      alignItems="flex-start"
      mb="$8x"
    >
      <Box flexShrink={0}>
        <IconExclamationCircle
          size={16}
          color={getCSSVariable('$contentStateErrorDefault', 'color')}
        />
      </Box>
      <Box flexDirection="column" gap="$2x" flex={1}>
        <Text tag="span" typography="$labelSmall" color="$contentStateErrorDefault">
          This home is not for rent
        </Text>
        <Text tag="span" typography="$bodySmall" color="$contentStateErrorDefault">
          If someone asked you to pay a deposit or rent for this home, you may be the target of a
          scam. Opendoor does not rent homes.{' '}
          <Link
            href="/rental-scam"
            analyticsName="rental-scam-banner-learn-more"
            typography="$bodySmall"
            color="$contentStateErrorDefault"
            style={{ textDecoration: 'underline' }}
            onPress={() => {
              trackJourneyEvent('marketplaceProperty', 'rentalScamBannerLearnMore', {
                addressUuid,
              });
            }}
          >
            Learn more
          </Link>
        </Text>
      </Box>
    </Box>
  );
};
